@if (roomData$ | async; as roomData) {
	<div data-test-id="room-detail">
		<div class="flex flex-col">
			<vp-carousel
				[contentMargin]="roomData.isBookable ? 'small' : 'none'"
				[items]="images"
				data-test-id="room-detail-carousel"
			/>
			@if (roomData.isBookable) {
				<div class="z-10 -mt-10 px-3 md:px-8">
					<div class="section-inner">
						<sb-availability-widget
							[currency]="hotelStore.currency()"
							[hotelGuid]="roomData.hotel.guid"
							[hotelSlug]="this.hotelSlug"
							[language]="this.currentLanguage()"
							[occupancyRestrictions]="
								roomData.occupancyRestrictions
							"
							[roomGuid]="roomData.guid"
							[roomSlug]="roomSlug"
						/>
					</div>
				</div>
			}
		</div>
		<section class="section-spacing bg-white">
			<div class="section-inner section-gap flex-col">
				<div class="column-gap flex-col lg:flex-row">
					<div class="flex-col lg:w-2/3">
						<div>
							<header>
								<h1
									class="heading-group heading-group-lg"
									data-test-id="room-detail-title"
								>
									{{ roomData.title }}
								</h1>
								<p
									class="heading-group-overline"
									role="doc-subtitle"
								>
									{{ roomData.category }}
								</p>
							</header>
							<vp-icon-list
								[items]="roomFacilityIconList"
								data-test-id="room-detail-facilities"
							/>
						</div>
						<div class="flex flex-row gap-3">
							<vp-tag color="accent">
								{{
									'global.checkin-time'
										| translate
											: {
													time: roomData.checkInTime,
											  }
								}}
							</vp-tag>
							<vp-tag color="accent">
								{{
									'global.checkout-time'
										| translate
											: {
													time: roomData.checkOutTime,
											  }
								}}
							</vp-tag>
						</div>
						<vp-read-more>
							<markdown
								[data]="roomData.description"
								class="markdown"
								data-test-id="room-detail-description"
							/>
						</vp-read-more>
						<div
							class="list-checkmarks list-grid flex w-full flex-col gap-4 md:gap-8"
						>
							<h2 class="running-head mb-0">
								{{ 'global.room' | translate }}
								{{ 'global.facilities' | translate }}
							</h2>
							<vp-list-grid
								[listItems]="roomFacilities"
								[name]="'room-facilities'"
								[sheetTitle]="
									('global.room' | translate) +
									' ' +
									('global.facilities' | translate)
								"
								[useCol2]="true"
							/>
						</div>
					</div>
					<div class="flex-col lg:w-1/3">
						@if (roomData.isBookable) {
							<vp-persuade-card
								[ctaForceReload]="!bookingtool"
								[ctaText]="'global.book-directly' | translate"
								[ctaUrl]="roomData.bookingToolRedirectUrl"
								[ctaUrlQueryParams]="
									roomData.bookingToolQueryParams
								"
								[ctaVariant]="'btn-outline'"
								[price]="roomData.price"
								[pricingToolTip]="{
									occupancy: roomData.pricingToolTipOccupancy,
									items: [
										{
											name: roomData.title,
											price: roomData.price
												?.amountExcludingCharges,
										},
									],
								}"
								[reversedCtaColumn]="true"
								[title]="
									'global.book-accommodation-type'
										| translate
											: {
													type:
														([
															roomData.accommodationType,
														]
														| accommodationType
														| translate),
											  }
								"
								[useVerticalCard]="true"
								data-test-id="cta-card"
								headingType="h2"
							>
								<ng-container content>
									<div class="list-checkmarks">
										<ul>
											<li data-test-id="cta-card-checkin">
												{{
													'global.checkin-time'
														| translate
															: {
																	time: roomData.checkInTime,
															  }
												}}
											</li>
											<li
												data-test-id="cta-card-checkout"
											>
												{{
													'global.checkout-time'
														| translate
															: {
																	time: roomData.checkOutTime,
															  }
												}}
											</li>
											<li>
												{{
													'global.best-price-guarantee'
														| translate
												}}
											</li>
										</ul>
									</div>
								</ng-container>
							</vp-persuade-card>
						}
						@if (roomData.hotel?.rating?.score) {
							<vp-rating
								[isHorizontal]="true"
								[numberOfRatings]="
									roomData.hotel.rating.numberOfRatings
								"
								[score]="roomData.hotel.rating.score"
							/>
						}
					</div>
				</div>
				<div class="column-gap flex-col lg:flex-row">
					<div class="flex-col lg:w-2/3">
						@if (roomData.hotel) {
							<h2
								class="running-head running-head-center"
								data-test-class="hotel-header"
							>
								{{
									'storyblok.pages.hotel-information'
										| translate
								}}
							</h2>
							@if (roomData.hotel.images?.length > 0) {
								<vp-mini-carousel
									[items]="hotelImages"
									aspectRatio="21:9"
								/>
							}
							<vp-tab-menu
								color="white"
								data-test-id="room-hotel-info"
							>
								<vp-tab-menu-item
									[content]="facilitiesRef"
									[label]="'global.facilities' | translate"
									name="facilities"
								/>
								<vp-tab-menu-item
									[content]="goodToKnowsRef"
									[label]="'global.good-to-know' | translate"
									name="good-to-knows"
								/>
								@if (
									this.hotelStore.address().latitude &&
									this.hotelStore.address().longitude
								) {
									<vp-tab-menu-item
										[content]="mapRef"
										[label]="'global.map' | translate"
										name="map"
									/>
								}
							</vp-tab-menu>
							<ng-template #facilitiesRef>
								<vp-list-grid
									[listItems]="
										roomData.hotel?.facilities || []
									"
									[name]="'hotel-facilities'"
									[sheetTitle]="
										('global.hotel.label' | translate) +
										' ' +
										('global.facilities' | translate)
									"
									[useCol2]="true"
								/>
							</ng-template>
							<ng-template #goodToKnowsRef>
								<vp-list-grid
									[listItems]="
										roomData.hotel?.goodToKnows || []
									"
									[name]="'hotel-good-to-knows'"
									[sheetTitle]="
										'global.good-to-know' | translate
									"
									[useCol2]="false"
								/>
							</ng-template>
							<ng-template #mapRef>
								<vp-map
									[lat]="this.hotelStore.address().latitude"
									[long]="this.hotelStore.address().longitude"
								/>
							</ng-template>
						}
					</div>
					<div class="lg:w-1/3"></div>
				</div>
			</div>
		</section>
	</div>
}
